/**
 * Sub-pass 2B — Owner Console analytics + broker survey report.
 *
 * Renders two side-by-side sections inside the Owner tab strip:
 *   1. Four all-time analysis counters + Tracking-since timestamp.
 *   2. Broker survey report with plan filter (All / Free / Pro / Premium).
 *
 * Uses the same ``adminHeaders`` pattern as the rest of the Owner
 * Console. Fails soft with visible error state.
 */
import React, { useCallback, useEffect, useState } from "react";
import { BarChart3, Loader2, RefreshCw, Users, Sparkles, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

function adminHeaders() {
  return { "X-Admin-Request": "1" };
}

const PLAN_OPTIONS = [
  { value: "all", label: "All users" },
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "premium", label: "Premium" },
];

export default function OwnerAnalyticsPanel() {
  const [counters, setCounters] = useState(null);
  const [countersErr, setCountersErr] = useState("");
  const [countersLoading, setCountersLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportErr, setReportErr] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [planFilter, setPlanFilter] = useState("all");

  const loadCounters = useCallback(async () => {
    setCountersLoading(true);
    setCountersErr("");
    try {
      const res = await api.get("/admin/analytics/analysis-counters", { headers: adminHeaders() });
      setCounters(res.data || null);
    } catch (ex) {
      setCountersErr(ex?.response?.data?.detail || ex?.message || "Failed to load counters.");
    } finally {
      setCountersLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (plan) => {
    setReportLoading(true);
    setReportErr("");
    try {
      const res = await api.get("/admin/broker-survey/report", {
        headers: adminHeaders(),
        params: { plan },
      });
      setReport(res.data || null);
    } catch (ex) {
      setReportErr(ex?.response?.data?.detail || ex?.message || "Failed to load broker report.");
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => { loadCounters(); loadReport(planFilter); }, [loadCounters, loadReport, planFilter]);

  return (
    <div className="space-y-6" data-testid="owner-analytics-panel">
      {/* Analysis counters */}
      <section
        className="rounded-xl border border-border/60 bg-card/60 p-4 sm:p-5"
        data-testid="owner-analysis-counters"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#E0B341]" />
            <h3 className="font-display text-base text-foreground">Analysis metrics</h3>
          </div>
          <Button size="sm" variant="ghost" onClick={loadCounters} disabled={countersLoading} data-testid="owner-analysis-counters-refresh">
            {countersLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {countersErr ? (
          <div className="text-xs text-[#E5484D]" data-testid="owner-analysis-counters-error">{String(countersErr)}</div>
        ) : counters ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <MetricCard
                label="Total completed analyses"
                value={counters.total_analyses}
                tooltip="Every successful, user-initiated analysis from a real member. Repeated analyses of the same stock count again. Excludes previews, background refreshes, admin activity, and failed requests."
                testId="metric-total-analyses"
              />
              <MetricCard
                label="Unique analyzed stocks"
                value={counters.unique_symbols}
                tooltip="Distinct canonical stock symbols represented by qualifying analysis events."
                testId="metric-unique-symbols"
              />
              <MetricCard
                label="Total Opportunities found"
                value={counters.total_opportunities}
                tooltip="Successful qualifying analyses classified as Opportunity or Top Opportunity by the canonical backend classification."
                testId="metric-total-opportunities"
              />
              <MetricCard
                label="Unique Opportunity stocks"
                value={counters.unique_opportunity_symbols}
                tooltip="Distinct symbols that ever produced an Opportunity or Top Opportunity result. A later Neutral or Risk result does not erase them."
                testId="metric-unique-opportunity-symbols"
              />
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground" data-testid="metric-tracking-since">
              {counters.tracking_since ? (
                <>Tracking since <span className="tabular-nums">{new Date(counters.tracking_since).toISOString().slice(0, 10)}</span> (prospective; historical events before this date are not reconstructed).</>
              ) : (
                <>Tracking begins with the first qualifying production analysis event.</>
              )}
            </div>
          </>
        ) : countersLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="text-xs text-muted-foreground">No data yet.</div>
        )}
      </section>

      {/* Broker survey report */}
      <section
        className="rounded-xl border border-border/60 bg-card/60 p-4 sm:p-5"
        data-testid="owner-broker-report"
      >
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#E0B341]" />
            <h3 className="font-display text-base text-foreground">Broker survey report</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Users className="w-3 h-3" />Plan</span>
            <Select value={planFilter} onValueChange={(v) => setPlanFilter(v)}>
              <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="owner-broker-plan-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`owner-broker-plan-${opt.value}`}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={() => loadReport(planFilter)} disabled={reportLoading} data-testid="owner-broker-refresh">
              {reportLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        {reportErr ? (
          <div className="text-xs text-[#E5484D]" data-testid="owner-broker-report-error">{String(reportErr)}</div>
        ) : reportLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : report ? (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground" data-testid="owner-broker-respondents">
              Respondents: <span className="text-foreground tabular-nums">{report.respondents ?? 0}</span> · plan filter: <Badge variant="outline" className="ml-1 text-[10px] uppercase">{report.plan || "all"}</Badge>
            </div>
            {report.respondents === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="owner-broker-empty">No respondents in this filter yet.</div>
            ) : (
              <>
                <div className="text-[11px] text-muted-foreground italic">{report.multi_select_note}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="owner-broker-list">
                  {(report.brokers || []).map((row) => (
                    <div key={row.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border/50 bg-background/70" data-testid={`owner-broker-row-${row.id}`}>
                      <span className="text-foreground truncate">{row.label}</span>
                      <span className="tabular-nums text-muted-foreground ml-2 shrink-0">{row.count} · {row.pct}%</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border/50 bg-background/70" data-testid="owner-broker-no-broker">
                  <span className="text-foreground">I don't use a broker yet</span>
                  <span className="tabular-nums text-muted-foreground ml-2 shrink-0">{report.no_broker?.count ?? 0} · {report.no_broker?.pct ?? 0}%</span>
                </div>
                {(report.other || []).length > 0 && (
                  <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Other (free text)</div>
                    <div className="space-y-1" data-testid="owner-broker-other-list">
                      {report.other.map((row) => (
                        <div key={row.text} className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border/50 bg-background/70">
                          <span className="text-foreground truncate">{row.text}</span>
                          <span className="tabular-nums text-muted-foreground ml-2 shrink-0">{row.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">No data.</div>
        )}
      </section>
    </div>
  );
}

function MetricCard({ label, value, tooltip, testId }) {
  return (
    <div
      className="rounded-lg border border-border/50 bg-background/70 p-3"
      data-testid={testId}
      title={tooltip}
    >
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1">
        {label}
        <HelpCircle className="w-3 h-3 opacity-60" aria-hidden />
      </div>
      <div className="mt-1 text-2xl font-display tabular-nums text-foreground">{Number(value ?? 0).toLocaleString()}</div>
    </div>
  );
}
